import { useState, useEffect } from 'react';
import { Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import { useEffectiveUser } from '../hooks/useEffectiveUser';
import { supabase } from '../lib/supabaseClient';
import Header from '../components/Header';
import Button from '../components/Button';
import SuccessModal from '../components/SuccessModal';

interface FormErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  general?: string;
}

export default function ChangePassword() {
  const { navigateTo, showToast } = useApp();
  const { user, isLoggedOut, isPreviewMode } = useEffectiveUser();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (isLoggedOut || !user) {
      navigateTo('profile-settings');
    }
  }, [isLoggedOut, user, navigateTo]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!currentPassword.trim()) {
      newErrors.currentPassword = 'Please enter your current password.';
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = 'Please enter a new password.';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters.';
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password.';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePassword = async () => {
    setErrors({});

    if (!validate()) {
      return;
    }

    if (isPreviewMode) {
      showToast('Preview mode: Password change disabled');
      return;
    }

    setIsSubmitting(true);

    try {
      // First, verify the current password by attempting to sign in
      if (!user?.email) {
        setErrors({ general: 'No email found. Please try signing in again.' });
        setIsSubmitting(false);
        return;
      }

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        console.error('Current password verification error:', signInError);
        setErrors({ general: 'The current password you entered is incorrect. Please try again.' });
        setIsSubmitting(false);
        return;
      }

      // Now update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error('Password update error:', updateError);

        // Handle specific error cases
        if (updateError.message.includes('same')) {
          setErrors({ general: 'New password must be different from your current password.' });
        } else {
          setErrors({ general: updateError.message || 'Failed to update password. Please try again.' });
        }

        setIsSubmitting(false);
        return;
      }

      // Success - clear form and show modal
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Unexpected error during password update:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    navigateTo('profile-settings');
  };

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title="Change Password"
        showBackButton
        onBackClick={() => navigateTo('profile-settings')}
      />

      <div className="px-6 py-6">
        <div className="max-w-[600px] mx-auto">
          <div className="bg-[#181818] rounded-xl border border-[#282828] p-6" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
            <div className="flex items-center gap-3 mb-2">
              <Lock className="w-5 h-5 text-[#50B3A8]" />
              <h2 className="text-[17px] font-semibold text-[#F5F5F5]">
                Update your password
              </h2>
            </div>

            <p className="text-[11px] text-[#606060] mb-6 leading-relaxed">
              Password must be at least 8 characters long.
            </p>

            {errors.general && (
              <div className="mb-6 p-4 bg-[#FF6F61]/10 border border-[#FF6F61]/20 rounded-xl">
                <p className="text-[#FF6F61] text-sm">{errors.general}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-[12px] text-[#909090] mb-2">
                  Current password
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      if (errors.currentPassword) {
                        setErrors({ ...errors, currentPassword: undefined });
                      }
                    }}
                    placeholder="Enter current password"
                    className={`w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 pr-12 text-[15px] border ${
                      errors.currentPassword ? 'border-[#FF6F61]' : 'border-[#282828]'
                    } hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.currentPassword && (
                  <p className="text-[#FF6F61] text-[12px] mt-1.5">{errors.currentPassword}</p>
                )}
              </div>

              {/* New Password */}
              <div>
                <label className="block text-[12px] text-[#909090] mb-2">
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (errors.newPassword) {
                        setErrors({ ...errors, newPassword: undefined });
                      }
                    }}
                    placeholder="Enter new password"
                    className={`w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 pr-12 text-[15px] border ${
                      errors.newPassword ? 'border-[#FF6F61]' : 'border-[#282828]'
                    } hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="text-[#FF6F61] text-[12px] mt-1.5">{errors.newPassword}</p>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-[12px] text-[#909090] mb-2">
                  Confirm new password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors({ ...errors, confirmPassword: undefined });
                      }
                    }}
                    placeholder="Confirm new password"
                    className={`w-full bg-black/30 text-[#F5F5F5] rounded-lg px-3.5 py-2.5 pr-12 text-[15px] border ${
                      errors.confirmPassword ? 'border-[#FF6F61]' : 'border-[#282828]'
                    } hover:border-[#383838] focus:border-[#40E0D0] focus:ring-1 focus:ring-[#40E0D0]/15 focus:outline-none transition-all`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-[#FF6F61] text-[12px] mt-1.5">{errors.confirmPassword}</p>
                )}
              </div>

              <div className="pt-4">
                <Button
                  fullWidth
                  onClick={handleChangePassword}
                  disabled={isSubmitting}
                  variant="primary"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onDismiss={handleModalClose}
        title="Password updated"
        message="Your password has been changed successfully."
      />
    </div>
  );
}
